import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { inertiaUrlTab } from "@/lib/utils"
import { formatPeriodKey, formatMonthKey } from "@/lib/period-format"
import { ChevronRight, Flag, Pencil, Plus, Printer, Target } from "lucide-react"
import { MemberLayout } from "./components/MemberLayout"
import { HiddenAssessmentsState } from "./components/HiddenAssessmentsState"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import { RockGrid } from "@/components/planning/RockCard"
import { KpiScorecard } from "@/components/planning/KpiScorecard"
import { RockFinalizeModal } from "@/components/planning/RockFinalizeModal"
import { useRockUpdate } from "@/hooks/use-rock-update"
import { useMonthlyResultUpdate } from "@/hooks/use-monthly-result-update"
import { ImperativeDialog } from "@/components/group-features/ImperativeDialog"
import { ImperativeDetailDialog } from "@/components/group-features/ImperativeDetailDialog"
import { KpiEditDialog } from "@/components/planning/KpiEditDialog"
import { OneYearPlanEditDialog } from "@/components/planning/OneYearPlanEditDialog"
import {
  StartNewCycleDialog,
} from "@/components/planning/StartNewCycleDialog"
import { AssessmentReminderBanner } from "@/components/planning/AssessmentReminderBanner"
import { WheelSnapshotHistorySection } from "@/components/planning/WheelSnapshotHistorySection"

function formatImpDate(dateString) {
  if (!dateString) return ""
  const date = new Date(dateString + "T00:00:00")
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatThisYearDate(dateString) {
  if (!dateString) return "—"
  const date = new Date(dateString + "T00:00:00")
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

// A rock is "wrap-up eligible" in the final 2 weeks of the quarter and
// for the first week after (so the member has a brief grace window to
// come back and finalize). Matches the copy "Wrap up this quarter."
function isWrapUpWindow(quarterEndAt) {
  if (!quarterEndAt) return false
  const end = new Date(quarterEndAt).getTime()
  const now = Date.now()
  const twoWeeksMs = 14 * 24 * 60 * 60 * 1000
  const oneWeekMs = 7 * 24 * 60 * 60 * 1000
  return now >= end - twoWeeksMs && now <= end + oneWeekMs
}

// Member home = Kingdom Factor planning dashboard. Two tabs:
//   Business Plan — active imperatives (in a group), rocks, KPI/KOI scorecard, yearly targets
//   Life Audit — Integrated Life Audit, snapshot timeline, retake CTA
// Feature surfaces (Content, Prayer, Crucial Conversations, Godly Council,
// Imperatives) live in the sidebar, not on this page.
export default function Dashboard({
  currentUser,
  annualPlan,
  currentQuarter,
  currentMonth,
  scorecard,
  latestWheelSnapshot = null,
  wheelSnapshots = [],
  assessmentReminder = null,
  quarterKey,
  monthKey,
  imperatives = [],
  isCoach = false,
  groupMembers = [],
  intake = null,
  // The active group's access level (R4–R7): none / wheel_only / plan_only /
  // full. Fail closed to "none" — the most restrictive state — if a future
  // render path ever omits it.
  assessmentAccess = "none",
}) {
  const { props, url } = usePage()
  const { currentGroup } = props
  // Per-surface flags derived from the level (mirrors Planning::AssessmentVisibility).
  const wheelAccessible = assessmentAccess === "wheel_only" || assessmentAccess === "full"
  const planAccessible = assessmentAccess === "plan_only" || assessmentAccess === "full"
  const dashboardTab = inertiaUrlTab(url) === "assessment" ? "assessment" : "plan"
  const hasPlan = Boolean(annualPlan)

  // Imperative dialog state — shared across the "Active Imperatives" card
  // mount points (create button, empty-state CTA, row click). Editing
  // routes through `editingImperative`; null means create mode.
  const [impDialogOpen, setImpDialogOpen] = useState(false)
  const [editingImperative, setEditingImperative] = useState(null)
  const [impDialogKey, setImpDialogKey] = useState(0)

  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedImperative, setSelectedImperative] = useState(null)
  const [metTarget, setMetTarget] = useState(null)
  const [missedTarget, setMissedTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const openDetailImperative = (imperative) => {
    setSelectedImperative(imperative)
    setDetailOpen(true)
  }

  const handleMarkMet = () => {
    if (!metTarget) return
    router.post(`/app/member/imperatives/${metTarget.id}/complete`, {}, {
      preserveScroll: true,
      onSuccess: () => setMetTarget(null),
    })
  }

  const handleMarkMissed = () => {
    if (!missedTarget) return
    router.post(`/app/member/imperatives/${missedTarget.id}/miss`, {}, {
      preserveScroll: true,
      onSuccess: () => setMissedTarget(null),
    })
  }

  const handleDeleteImperative = () => {
    if (!deleteTarget) return
    router.delete(`/app/member/imperatives/${deleteTarget.id}`, {
      preserveScroll: true,
      onSuccess: () => setDeleteTarget(null),
    })
  }

  const openCreateImperative = () => {
    setEditingImperative(null)
    setImpDialogKey((k) => k + 1)
    setImpDialogOpen(true)
  }

  const openEditImperative = (imperative) => {
    setEditingImperative(imperative)
    setImpDialogKey((k) => k + 1)
    setImpDialogOpen(true)
  }

  const refreshImperatives = () => {
    router.reload({ only: ["imperatives"] })
  }

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="My dashboard — Kingdom Factor" />

      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-zinc-900">
              Welcome, {currentUser?.name?.split(" ")[0] || "Member"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {planAccessible ? (
                <>
                  {formatPeriodKey(quarterKey, { year: false })} · {formatMonthKey(monthKey)}
                  {currentGroup ? ` · ${currentGroup.name}` : ""}
                </>
              ) : (
                currentGroup?.name || ""
              )}
            </p>
          </div>
        </header>

        {/* None (R7): no assessment surfaces — the group hub welcomes the
            member and links the non-assessment features they DO have. */}
        {assessmentAccess === "none" && <HiddenAssessmentsState />}

        {/* Integrated Life Audit only (R5): lead with the audit; no plan CTAs. */}
        {assessmentAccess === "wheel_only" && (
          <>
            <AssessmentReminderBanner reminder={assessmentReminder} />
            <LifeAssessmentCard
              latestWheelSnapshot={latestWheelSnapshot}
              currentQuarter={currentQuarter}
            />
            {wheelSnapshots.length > 0 && (
              <WheelSnapshotHistorySection snapshots={wheelSnapshots} embedded />
            )}
          </>
        )}

        {/* Plan only (R6): the plan builder / progress; no wheel card. */}
        {assessmentAccess === "plan_only" &&
          (hasPlan ? (
            <PlanProgressContent
              currentGroup={currentGroup}
              imperatives={imperatives}
              currentQuarter={currentQuarter}
              scorecard={scorecard}
              currentMonth={currentMonth}
              annualPlan={annualPlan}
              onCreateImperative={openCreateImperative}
              onImperativeRowClick={openDetailImperative}
            />
          ) : (
            <PlanOnlyZeroState canStartGoalsOnly={!intake} />
          ))}

        {/* Full (R4): unchanged — the member self-selects full / plan-only /
            wheel-alone via the two-tab layout. */}
        {assessmentAccess === "full" && (
          <>
            <AssessmentReminderBanner reminder={assessmentReminder} />

            {!hasPlan ? (
              <>
                <ZeroPlanState canStartGoalsOnly={!intake} />
                {/* A member who took the standalone Integrated Life Audit but never
                    completed a full plan has no AnnualPlan, so the
                    hasPlan-gated assessment tab below never renders. Surface
                    their latest wheel + snapshot timeline here so "see my
                    previous scores" isn't dead-ended on the empty-plan state. */}
                {wheelSnapshots.length > 0 && (
                  <>
                    <LifeAssessmentCard
                      latestWheelSnapshot={latestWheelSnapshot}
                      currentQuarter={currentQuarter}
                    />
                    <WheelSnapshotHistorySection snapshots={wheelSnapshots} embedded />
                  </>
                )}
              </>
            ) : (
              <Tabs
                value={dashboardTab}
                onValueChange={(v) =>
                  router.get(
                    "/app/member",
                    { tab: v },
                    { preserveState: true, preserveScroll: true, replace: true },
                  )
                }
              >
                <TabsList>
                  <TabsTrigger value="plan">Business Plan</TabsTrigger>
                  <TabsTrigger value="assessment">Life Audit</TabsTrigger>
                </TabsList>

                <TabsContent value="plan" className="space-y-6 mt-4">
                  <PlanProgressContent
                    currentGroup={currentGroup}
                    imperatives={imperatives}
                    currentQuarter={currentQuarter}
                    scorecard={scorecard}
                    currentMonth={currentMonth}
                    annualPlan={annualPlan}
                    onCreateImperative={openCreateImperative}
                    onImperativeRowClick={openDetailImperative}
                  />
                </TabsContent>

                <TabsContent value="assessment" className="space-y-6 mt-4">
                  <LifeAssessmentCard
                    latestWheelSnapshot={latestWheelSnapshot}
                    currentQuarter={currentQuarter}
                  />
                  <WheelSnapshotHistorySection snapshots={wheelSnapshots} embedded />
                </TabsContent>
              </Tabs>
            )}
          </>
        )}
      </div>

      {planAccessible && currentGroup && (
        <>
          <ImperativeDialog
            key={`imp-${impDialogKey}`}
            open={impDialogOpen}
            onOpenChange={setImpDialogOpen}
            imperative={editingImperative}
            groupMembers={groupMembers}
            isCoach={isCoach}
            onSuccess={refreshImperatives}
          />

          <ImperativeDetailDialog
            open={detailOpen}
            onOpenChange={setDetailOpen}
            imperative={selectedImperative}
            currentUserId={currentUser.id}
            onEdit={openEditImperative}
            onMarkMet={setMetTarget}
            onMarkMissed={setMissedTarget}
            onDelete={setDeleteTarget}
          />

          <AlertDialog
            open={!!metTarget}
            onOpenChange={(open) => !open && setMetTarget(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Mark as Met</AlertDialogTitle>
                <AlertDialogDescription>
                  Mark this imperative as met? The resolution date will be recorded.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={handleMarkMet}
                >
                  Mark Met
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog
            open={!!missedTarget}
            onOpenChange={(open) => !open && setMissedTarget(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Mark as Missed</AlertDialogTitle>
                <AlertDialogDescription>
                  Mark this imperative as missed? This signals the goal wasn&apos;t met by the due
                  date
                  {missedTarget?.consequence ? " — the consequence still applies." : "."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={handleMarkMissed}
                >
                  Mark Missed
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog
            open={!!deleteTarget}
            onOpenChange={(open) => !open && setDeleteTarget(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Imperative</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this imperative? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={handleDeleteImperative}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </MemberLayout>
  )
}

// The "Business Plan" content — shared by the Full dashboard's plan tab and
// the Plan-only homepage (which has no tabs). Active imperatives, rocks,
// KPI/KOI scorecards, and the yearly targets card.
function PlanProgressContent({
  currentGroup,
  imperatives,
  currentQuarter,
  scorecard,
  currentMonth,
  annualPlan,
  onCreateImperative,
  onImperativeRowClick,
}) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <a href="/app/planning/business_plan.pdf" target="_blank" rel="noopener noreferrer">
          <Button variant="kf-outline" size="sm">
            <Printer className="w-4 h-4 mr-1.5" />
            Print / PDF
          </Button>
        </a>
      </div>

      {currentGroup && (
        <ActiveImperativesCard
          imperatives={imperatives}
          onCreate={onCreateImperative}
          onRowClick={onImperativeRowClick}
        />
      )}

      <RocksSection currentQuarter={currentQuarter} />

      {scorecard && (
        <ScorecardsSection
          scorecard={scorecard}
          currentMonth={currentMonth}
          annualPlan={annualPlan}
        />
      )}

      <ThisYearCard annualPlan={annualPlan} />
    </div>
  )
}

function ActiveImperativesCard({ imperatives, onCreate, onRowClick }) {
  const isEmpty = imperatives.length === 0

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>Active Imperatives</CardTitle>
        {!isEmpty && (
          <Button
            type="button"
            variant="kf-outline"
            size="sm"
            onClick={onCreate}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="text-center py-6">
            <Target className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
            <p className="text-sm text-zinc-600 mb-3">
              No active imperatives.
            </p>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              onClick={onCreate}
            >
              <Plus className="w-4 h-4 mr-1" />
              Create your first imperative
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {imperatives.map((imp) => (
              <li
                key={imp.id}
                className="py-3 flex items-start justify-between gap-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded"
                onClick={() => onRowClick(imp)}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 line-clamp-2">
                    {imp.goal}
                  </p>
                  {imp.endDate && (
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Due {formatImpDate(imp.endDate)}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" aria-hidden />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function RocksSection({ currentQuarter }) {
  const { rocks, updateRock, appendRock, appendRockBusy, removeRock, error, clearError } = useRockUpdate(
    currentQuarter?.id,
    currentQuarter?.rocks || [],
  )
  const [finalizeOpen, setFinalizeOpen] = useState(false)
  const [finalizeMode, setFinalizeMode] = useState("wrap_up")
  const [rolloverWizardOpen, setRolloverWizardOpen] = useState(false)
  const [rolloverError, setRolloverError] = useState(null)
  const [deleteRockTarget, setDeleteRockTarget] = useState(null)

  if (!currentQuarter) return null

  const wrapUp = isWrapUpWindow(currentQuarter.quarterEndAt)
  const activeCount = rocks.filter((r) => !r.result).length

  const startNewCycle = () => {
    if (activeCount > 0) {
      setFinalizeMode("rollover")
      setFinalizeOpen(true)
    } else {
      setRolloverWizardOpen(true)
    }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg text-zinc-900">
            This quarter's rocks
          </h3>
          <p className="text-xs text-zinc-500">{formatPeriodKey(currentQuarter.quarterKey)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {wrapUp && activeCount > 0 && (
            <Button
              type="button"
              variant="gradient"
              size="sm"
              onClick={() => {
                setFinalizeMode("wrap_up")
                setFinalizeOpen(true)
              }}
            >
              <Flag className="w-4 h-4 mr-1.5" />
              Wrap up this quarter
            </Button>
          )}
          <Button
            type="button"
            variant="kf-outline"
            size="sm"
            onClick={startNewCycle}
            aria-label="Start a new quarter"
          >
            Start a new quarter
          </Button>
        </div>
      </div>
      {rolloverError && (
        <div
          role="alert"
          className="mb-3 flex items-center justify-between gap-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
        >
          <span>{rolloverError}</span>
          <button
            type="button"
            onClick={() => setRolloverError(null)}
            className="text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mb-3 flex items-center justify-between gap-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={clearError}
            className="text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}
      <RockGrid
        rocks={rocks}
        quarterStartAt={currentQuarter.quarterStartAt}
        quarterEndAt={currentQuarter.quarterEndAt}
        readOnly={false}
        onUpdate={updateRock}
        onAddRock={appendRock}
        addRockBusy={appendRockBusy}
        onRemoveRock={(rock) => setDeleteRockTarget(rock)}
      />
      <RockFinalizeModal
        open={finalizeOpen}
        onOpenChange={setFinalizeOpen}
        rocks={rocks}
        hookError={error}
        onFinalize={(rockId, result) => updateRock(rockId, "result", result)}
        mode={finalizeMode}
        onComplete={() => {
          if (finalizeMode === "rollover") {
            setRolloverWizardOpen(true)
          }
        }}
      />
      <StartNewCycleDialog
        open={rolloverWizardOpen}
        onOpenChange={setRolloverWizardOpen}
      />
      <AlertDialog
        open={!!deleteRockTarget}
        onOpenChange={(open) => !open && setDeleteRockTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rock?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRockTarget?.title
                ? `"${deleteRockTarget.title}" and its milestones will be permanently removed. This can't be undone.`
                : "This rock and its milestones will be permanently removed. This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                if (deleteRockTarget) removeRock(deleteRockTarget.id)
                setDeleteRockTarget(null)
              }}
            >
              Delete rock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* updateRock returns a Promise<boolean>; the modal awaits it. */}
    </section>
  )
}

// Both scorecards wrapped with an inline KPI/KOI status updater.
// Tiles show +/=/- buttons for the current month; clicks optimistically
// update local state then PATCH /app/planning/monthly_check_ins/:id.
// When currentMonth is nil (member hasn't completed any annual plan
// yet) the scorecards render without the inline controls — Edit links
// still surface so they can fill in definitions first.
function ScorecardsSection({ scorecard, currentMonth, annualPlan }) {
  const { kpiResults, koiResults, updateResult, error, clearError } =
    useMonthlyResultUpdate(
      currentMonth?.id,
      currentMonth?.kpiResults || [],
      currentMonth?.koiResults || [],
    )

  const canLog = Boolean(currentMonth?.id)
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false)
  const [koiDialogOpen, setKoiDialogOpen] = useState(false)

  return (
    <div className="space-y-6">
      {error && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={clearError}
            className="text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}
      <div>
        <KpiScorecard
          items={scorecard.kpis}
          heading="Annual KPIs"
          description="Key Performance Indicators — the metrics and measurable components in your business that show its health and sustainability."
          kind="kpi"
          currentMonthResults={canLog ? kpiResults : undefined}
          onUpdate={canLog ? updateResult : undefined}
          emptyMessage="Add KPIs to your annual plan to see your trend here."
        />
        <EditButton onClick={() => setKpiDialogOpen(true)} label="Edit KPIs" className="mt-2" />
      </div>
      <div>
        <KpiScorecard
          items={scorecard.kois}
          heading="Annual KOIs"
          description="Key Obedience Indicators — the spiritual disciplines and habits that grow you closer to the Lord and help you walk in obedience."
          kind="koi"
          currentMonthResults={canLog ? koiResults : undefined}
          onUpdate={canLog ? updateResult : undefined}
          emptyMessage="Add KOIs to your annual plan to see your trend here."
        />
        <EditButton onClick={() => setKoiDialogOpen(true)} label="Edit KOIs" className="mt-2" />
      </div>

      {annualPlan && (
        <>
          <KpiEditDialog
            kind="kpi"
            open={kpiDialogOpen}
            onOpenChange={setKpiDialogOpen}
            plan={annualPlan}
          />
          <KpiEditDialog
            kind="koi"
            open={koiDialogOpen}
            onOpenChange={setKoiDialogOpen}
            plan={annualPlan}
          />
        </>
      )}
    </div>
  )
}

// Tiny right-aligned edit button that sits under a dashboard summary
// (KPI scorecard, KOI scorecard). Opens an inline edit dialog -- members
// no longer have to walk through the full intake to update definitions.
function EditButton({ onClick, label, className = "" }) {
  return (
    <div className={`flex justify-end ${className}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 text-xs text-kf-red hover:underline"
      >
        <Pencil className="w-3 h-3" />
        {label}
      </button>
    </div>
  )
}

// "This year" card on the Business Plan tab. Surfaces the one-year plan —
// targets, target date, measurables, and top issues — at a glance and opens
// an inline edit dialog instead of routing the member through the intake wizard.
function ThisYearCard({ annualPlan }) {
  const [open, setOpen] = useState(false)
  if (!annualPlan) return null

  const issues = (annualPlan.issues || []).filter((i) => (i?.text || "").trim())

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>This year</CardTitle>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 text-xs text-kf-red hover:underline shrink-0"
            aria-label="Edit one-year plan"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-zinc-500">Revenue target</p>
              <p className="text-lg font-medium">
                {annualPlan.revenueTarget
                  ? `$${Number(annualPlan.revenueTarget).toLocaleString()}`
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-zinc-500">Profit target</p>
              <p className="text-lg font-medium">
                {annualPlan.profitTarget
                  ? `$${Number(annualPlan.profitTarget).toLocaleString()}`
                  : "—"}
              </p>
            </div>
            {annualPlan.targetDate && (
              <div>
                <p className="text-zinc-500">Target date</p>
                <p className="text-lg font-medium">{formatThisYearDate(annualPlan.targetDate)}</p>
              </div>
            )}
          </div>

          {annualPlan.measurables && (
            <div>
              <p className="text-zinc-500">Measurables</p>
              <p className="whitespace-pre-line text-zinc-900">{annualPlan.measurables}</p>
            </div>
          )}

          {issues.length > 0 && (
            <div>
              <p className="text-zinc-500">Top issues to solve or address</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-zinc-900">
                {issues.map((issue, idx) => (
                  <li key={issue.id ?? idx}>{issue.text}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <OneYearPlanEditDialog
        open={open}
        onOpenChange={setOpen}
        plan={annualPlan}
      />
    </>
  )
}

// Renders the Life Assessment tab card. Reads the wheel from the latest
// WheelSnapshot if available; falls back to the current quarter's
// wheel_totals only when no snapshot exists yet (legacy / pre-backfill).
// Adds two CTAs: take the wheel again (standalone, doesn't disturb the
// active cycle) and retake CTA (standalone audit).
function LifeAssessmentCard({ latestWheelSnapshot, currentQuarter }) {
  const fromSnapshot = Boolean(latestWheelSnapshot?.wheelTotals)
  const wheelTotals = fromSnapshot
    ? latestWheelSnapshot.wheelTotals
    : currentQuarter?.wheelTotals
  const wheelTotalScore = fromSnapshot
    ? latestWheelSnapshot.wheelTotalScore
    : currentQuarter?.wheelTotalScore
  const lastTakenLabel = fromSnapshot && latestWheelSnapshot.takenAt
    ? new Date(latestWheelSnapshot.takenAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle>Integrated Life Audit</CardTitle>
          {lastTakenLabel && (
            <p className="text-xs text-zinc-500 mt-1">
              Last taken {lastTakenLabel}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link href="/app/member/balance_wheel/retake">
            <Button type="button" variant="kf-outline" size="sm">
              {wheelTotals ? "Complete the Integrated Life Audit Again" : "Complete the Integrated Life Audit"}
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {wheelTotals ? (
          <>
            <LifeWheelRadar scores={wheelTotals} size={280} />
            <div>
              <p className="text-sm text-zinc-500 mb-3">
                {wheelTotalScore}/150 total
              </p>
              <ul className="space-y-1 text-sm">
                {Object.entries(wheelTotals).map(([category, score]) => (
                  <li key={category} className="flex justify-between">
                    <span className="capitalize">{category}</span>
                    <span className="font-medium">{score}/25</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <div className="col-span-2 space-y-3">
            <p className="text-zinc-500 italic">
              You haven&apos;t completed the Integrated Life Audit yet — it takes about five
              minutes and stands on its own.
            </p>
            <Link href="/app/member/balance_wheel/retake">
              <Button type="button" variant="gradient" size="sm">
                Complete the Integrated Life Audit
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Zero-state for a Plan-only member with no plan yet. Routes into the
// goals_only intake (wheel-free) — never the full assessment, which would walk
// the Integrated Life Audit this member's level hides.
function PlanOnlyZeroState({ canStartGoalsOnly = false }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Let's build your plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-zinc-700">
          Your dashboard comes to life once you build your one-year plan — your
          goals, KPIs, and this quarter's rocks. It takes about 10 minutes.
        </p>
        <Link href={canStartGoalsOnly ? "/app/plan/intake?mode=goals_only" : "/app/plan/intake"}>
          <Button variant="gradient" className="w-full sm:w-auto">
            {canStartGoalsOnly ? "Build my plan" : "Continue building your plan"}
          </Button>
        </Link>
      </CardContent>
    </Card>
  )
}

function ZeroPlanState({ canStartGoalsOnly = false }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Let's set up your plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-zinc-700">
          Your dashboard comes to life once you complete the Kingdom Factor
          assessment — a 15–20 minute walk through your Life Integration
          Audit, one-year plan, and this quarter's rocks.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Link href="/app/plan/intake">
            <Button variant="gradient" className="w-full sm:w-auto">
              Start the full assessment
            </Button>
          </Link>
          {/* goals_only self-start only applies before any intake exists —
              mirror the server-side eligibility so this never dead-ends. */}
          {canStartGoalsOnly && (
            <Link href="/app/plan/intake?mode=goals_only">
              <Button variant="kf-outline" className="w-full sm:w-auto">
                Build your plan without the wheel
              </Button>
            </Link>
          )}
        </div>
        <p className="text-sm text-zinc-500">
          Or{" "}
          <Link
            href="/app/member/balance_wheel/retake"
            className="text-kf-red hover:underline"
          >
            just complete the Integrated Life Audit
          </Link>{" "}
          on its own — you can build the rest of your plan anytime.
        </p>
      </CardContent>
    </Card>
  )
}
